// Balles en 3D sur une carte 2D : la balle suit le rayon au sol, sa hauteur varie avec l'inclinaison de la visée.
// Les murs arrêtent tout ; le couvert bas (muret, voiture, grillage…) laisse passer ce qui file au-dessus de lui.
import { raycast, rayRectSpan, type RayHit } from './geometry';
import type { GameMap } from './map';
import { INFECTED } from './constants';

// Hauteur de l'arme (bouche du canon) et du sommet du corps, debout et accroupi
export const muzzleHeight = (crouch: boolean) => (crouch ? 1.0 : 1.45);
export const bodyTop = (crouch: boolean) => (crouch ? 1.25 : 1.8);
export const MAX_PITCH = 1.2; // inclinaison de la visée (radians), vers le haut ou le bas

// Sommet du corps d'un infecté (le rampant est au sol)
export const infectedTop = (kind: number) => (kind === 4 ? 0.6 : 1.8 * (INFECTED[kind]?.scale ?? 1));

export type HitZone = 'head' | 'thorax' | 'limbs';
// Zone touchée selon la hauteur de la balle (null : passe au-dessus ou en dessous)
export function zoneAt(y: number, top: number): HitZone | null {
  if (y < 0 || y > top + 0.05) return null;
  if (y > top - 0.3) return 'head';
  return y > top * 0.5 ? 'thorax' : 'limbs';
}

export interface BulletHit extends RayHit { y: number } // y : hauteur de la balle au point d'arrêt

// h0 : hauteur de départ ; slope : montée par mètre parcouru (tan de l'inclinaison)
export function bulletRay(map: GameMap, ox: number, oy: number, ang: number, max: number, h0: number, slope: number): BulletHit {
  // Vers le bas, la balle finit dans le sol
  if (slope < -1e-4) max = Math.min(max, h0 / -slope);
  const dx = Math.cos(ang), dy = Math.sin(ang);
  let from = 0, near = 1.1;
  for (let guard = 0; guard < 12; guard++) {
    const hit = raycast(map, ox + dx * from, oy + dy * from, ang, max - from, true, near);
    const t = from + hit.t;
    const R = hit.rect;
    if (R && R.kind === 'low') {
      // Au-dessus du couvert sur toute sa traversée : la balle continue
      const [t0, t1] = rayRectSpan(ox, oy, dx, dy, R);
      const top = R.height ?? 1;
      if (Math.min(h0 + slope * t0, h0 + slope * t1) > top) { from = t1 + 0.02; near = 0; continue; }
    }
    return { t, rect: R, y: h0 + slope * t };
  }
  return { t: max, rect: null, y: h0 + slope * max };
}
